import { useEffect, useState } from "react"
import { AlertTriangle, ExternalLink, Ticket } from "lucide-react"
import { Button } from "@/components/ui/button"
import RaiseGitlabIssueDialog from "./RaiseGitlabIssueDialog"
import { useAuth } from "@/hooks/useAuth"
import {
  buildValidationFailureIssueTitle,
  loadIssuesCache,
  type GitlabIssue,
} from "@/services/gitlabService"
import type { ValidationFailurePoint } from "@/services/parasiteRateService"

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ClusterErrorCardProps {
  /** The selected validation failure point whose warnings should be shown. */
  point: ValidationFailurePoint
  /** The full set of validation failure points currently loaded.*/
  allValidationFailures: ValidationFailurePoint[]
}

/**
 * ClusterErrorCard renders a compact, red-outlined card that surfaces the
 * active validation warnings for the currently selected cluster while the map
 * is in error mode.
 *
 * When no GitLab issue exists yet for this cluster it exposes a "Raise GitLab
 * issue" action that opens a dialog for escalating the failure. When an issue
 * with the cluster's expected title already exists the action becomes a "Go to GitLab ticket" link to that
 * existing issue instead.
 *
 * @param props - The selected ValidationFailurePoint and the full list of
 *   validation failures used by the issue dialog.
 * @returns A red-outlined error card element, or null when there is no error.
 */
export default function ClusterErrorCard({ point, allValidationFailures }: ClusterErrorCardProps): React.JSX.Element | null {
  const { getAccessToken } = useAuth()

  // Controls visibility of the "Raise GitLab issue" dialog.
  const [issueDialogOpen, setIssueDialogOpen] = useState<boolean>(false)

  // The existing GitLab issue for this cluster, if one was found in the cache.
  // null means "no matching issue" (so the raise action is shown).
  const [existingIssue, setExistingIssue] = useState<GitlabIssue | null>(null)

  // The canonical issue title for this cluster
  const expectedIssueTitle: string = buildValidationFailureIssueTitle({
    country: point.country,
    sourceId: point.source_id1,
  })

  const validationError: string | null = point.validation_error
  const hasValidationError: boolean = validationError != null && validationError.trim() !== ""

  // Look up whether an issue already exists for this cluster whenever the
  // expected title changes (i.e. a different cluster is selected). Skipped when
  // there is no validation error, since the card renders nothing in that case.
  useEffect(() => {
    if (!hasValidationError) {
      return
    }

    // Guards against a stale async result overwriting state after the selected
    // cluster has already changed.
    let cancelled: boolean = false

    async function lookupExistingIssue(): Promise<void> {
      try {
        const token: string = await getAccessToken()
        const issuesByTitle: Map<string, GitlabIssue> = await loadIssuesCache(token)
        if (cancelled) {
          return
        }
        setExistingIssue(issuesByTitle.get(expectedIssueTitle) ?? null)
      } catch (error) {
        if (cancelled) {
          return
        }
        // A lookup failure should not block the raise flow; fall back to
        // showing the "Raise GitLab issue" action by treating it as "no
        // existing issue".
        console.error("ClusterErrorCard: failed to look up existing GitLab issue:", error)
        setExistingIssue(null)
      }
    }

    lookupExistingIssue()

    return () => {
      cancelled = true
    }
  }, [hasValidationError, expectedIssueTitle, getAccessToken, issueDialogOpen])

  // The validation_error field is the source of the active warnings. When it
  // is null/empty there is nothing to warn about, so render nothing rather
  // than showing an empty error box.
  if (!hasValidationError) {
    return null
  }

  return (
    <div
      role="alert"
      className="w-80 overflow-hidden rounded-xl border-2 border-red-500 bg-background/80 shadow-xl ring-1 ring-black/5 backdrop-blur-md"
    >
      <div className="flex items-start gap-2 px-4 py-3">
        <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" aria-hidden="true" />
        <p className="text-xs text-red-500 break-words min-w-0">{validationError}</p>
      </div>
      <div className="flex justify-center px-4 pb-3">
        {existingIssue !== null ? (
          // An issue already exists: link straight to it rather than offering
          // to create a duplicate.
          <Button
            asChild
            variant="outline"
            size="sm"
          >
            <a
              href={existingIssue.webUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Go to GitLab ticket #${existingIssue.iid} for this validation failure`}
            >
              <ExternalLink aria-hidden="true" />
              Go to GitLab ticket
            </a>
          </Button>
        ) : (
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={() => setIssueDialogOpen(true)}
            aria-label="Raise GitLab issue for this validation failure"
          >
            <Ticket aria-hidden="true" />
            Raise GitLab issue
          </Button>
        )}
      </div>

      <RaiseGitlabIssueDialog
        open={issueDialogOpen}
        onOpenChange={setIssueDialogOpen}
        point={point}
        allValidationFailures={allValidationFailures}
      />
    </div>
  )
}
